import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { PRINCIPALE, contesto, traduci, useTraduzioni } from '../../i18n';
import { messaggioErrore, servizioAssente } from '../accesso/campi';
import { CATALOGO, controllaFile, etichettaPeriodo, preparaFile, sceltePeriodo } from '../../utils/documenti';
import { adessoRoma } from '../../utils/percorso';
import ConfermaDocumento from './ConfermaDocumento';

// Caricamento multiplo con smistamento automatico (backend/lotti_documenti.py): fino a 20 file, senza dire prima
// che cosa sono. Ogni file sale da solo con la sua barra (due alla volta; se uno non va, gli altri continuano);
// il server legge in sottofondo (OCR) e il riepilogo si aggiorna da solo. Una riga per documento: anteprima,
// tipo in parole semplici, periodo, menu per correggere in un clic, "Conferma tutto". La miniatura apre l'anteprima
// grande (ConfermaDocumento): per lo staff i menu si spostano lì accanto, così si conferma guardando il documento.
// variante "cliente" = stile della pagina Documenti del cliente; "staff" = componenti dello staff.

export const MAX_FILE = 20;
const IN_PARALLELO = 2;
const OGNI_MS = 2000;
const MASSIMO_ATTESA_MS = 4 * 60 * 1000;

const STILI = {
  cliente: {
    scatola: 'card hero r lotto', titolo: 'ttl', riga: 'lotto-riga', mini: 'lotto-mini', testo: 'lotto-testo', meta: 'meta',
    barra: 'lotto-barra', piena: 'lotto-piena', select: 'lotto-select', bottone: 'btn sm', principale: 'btn sm p', errore: 'rifiuto', ok: 'pillola ok',
    attesa: 'pillola w', no: 'pillola no', piede: 'lotto-piede',
  },
  staff: {
    scatola: 'rounded-xl border border-gold/40 bg-card p-4 space-y-3', titolo: 'flex items-center gap-2 text-gold font-display text-base',
    riga: 'flex flex-wrap items-center gap-3 py-2 border-t border-gold/10 first:border-t-0', mini: 'w-12 h-14 rounded-md overflow-hidden bg-black/40 border border-gold/20 grid place-items-center text-[10px] text-white/50 flex-shrink-0',
    testo: 'flex-1 min-w-[180px] text-sm text-white', meta: 'block text-xs text-muted-foreground', barra: 'mt-1 block h-1.5 w-full bg-white/10 rounded overflow-hidden', piena: 'block h-full bg-gold',
    select: 'bg-card border border-gold/30 rounded-md px-2 py-1 text-xs text-white', bottone: 'text-xs px-3 py-1.5 rounded-md border border-gold/30 text-white hover:border-gold/60',
    principale: 'text-xs px-3 py-1.5 rounded-md bg-gold text-black font-semibold', errore: 'block text-xs text-red-300', ok: 'text-[11px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400',
    attesa: 'text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300', no: 'text-[11px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-300', piede: 'flex flex-wrap items-center justify-between gap-2 pt-2',
  },
};

// Il motivo di un errore: il testo del server per le risposte 4xx (il backend risponde sempre in italiano e passa
// da erroreIn, che lo traduce se la frase è nel dizionario); senza rete o con un 5xx "il servizio non risponde"
// (mai error.message: "Network Error", "Request failed with status code 500")
export const RETE = traduci(PRINCIPALE, 'documenti.lotto.rete');
export const motivoErrore = (e, ripiego = null, lingua = PRINCIPALE) => {
  const { t, errore } = contesto(lingua);
  if (servizioAssente(e)) return t('documenti.lotto.rete');
  const dettaglio = e.response.data?.detail;
  return typeof dettaglio === 'string' && dettaglio.trim()
    ? errore(dettaglio.trim()).replace(/\.$/, '') : (ripiego || t('documenti.lotto.non_riuscito'));
};
// "Non salvato: il servizio non risponde, riprovate tra poco." / "Non salvato: Tipo non valido. Riprovate."
const frase = (prima, e, dopo, lingua) => (servizioAssente(e)
  ? `${prima}: ${traduci(lingua, 'documenti.lotto.rete')}.` : `${prima}: ${motivoErrore(e, null, lingua)}. ${dopo}`);

// Caricamento di un documento (riga "Carica"): i controlli fatti prima di mandare i file (controllaFile) hanno già
// il loro messaggio in italiano; tutto il resto passa da messaggioErrore (rete e 5xx: "Il servizio non risponde…")
const messaggiLocali = (lingua) => [controllaFile([], lingua), controllaFile([{ size: Infinity }], lingua),
  controllaFile([{ size: 1, type: 'x/y', name: 'x' }], lingua)];
export const erroreCaricamento = (e, ripiego = null, lingua = PRINCIPALE) => (
  !e?.response && messaggiLocali(lingua).includes(e?.message)
    ? e.message : messaggioErrore(e, ripiego || traduci(lingua, 'documenti.non_caricato'), lingua));
// Errori che non passano riprovando (permessi, caricamento sparito): inutile aspettare 4 minuti
const definitivo = (e) => [401, 403, 404].includes(e?.response?.status);
export const ERRORE_RIEPILOGO = traduci(PRINCIPALE, 'documenti.lotto.errore_riepilogo');
// Un caricamento ancora in corso (i bottoni per sceglierne un altro aspettano); uno fermo (troppi file, servizio
// assente) o finito non blocca niente
export const lottoInCorso = (l) => Boolean(l && !l.fermo && !l.errore && !l.riepilogo?.pronto);
// Stesse parole di pages/DocumentiCliente.jsx (ASPETTATE): un caricamento alla volta
export const ASPETTATE_CARICAMENTO = traduci(PRINCIPALE, 'documenti.aspettate');
export const troppiFile = (n, lingua = PRINCIPALE) => traduci(lingua, 'documenti.lotto.troppi_file', { max: MAX_FILE, n });

// Il lotto: caricamento dei file, poi il riepilogo dal server finché è tutto letto
export const useLotto = (clienteId, onPronto, { attesaMassima = MASSIMO_ATTESA_MS, ogni = OGNI_MS } = {}) => {
  const { t, l: lingua } = useTraduzioni(); // la lingua del cliente; lo staff resta in italiano
  // {seq, id, file: [{nome, tipo, url, progresso, errore, fatto}], riepilogo, errore}; seq cambia a ogni caricamento
  // (il riquadro riparte da zero: niente errori o conferme del caricamento di prima)
  const [lotto, setLotto] = useState(null);
  const attivo = useRef(true);
  const corrente = useRef(null); // il lotto mostrato: "Chiudi" ferma l'attesa del riepilogo
  const seq = useRef(0);
  const inApertura = useRef(false); // POST /lotti in volo
  useEffect(() => () => { attivo.current = false; }, []);

  // Solo il file del SUO lotto: con due caricamenti di fila, le barre e gli errori del primo (ancora in volo) finivano
  // sulle righe del secondo
  const aggiornaFile = (id, i, cambi) => setLotto((l) => (l && l.id === id ? { ...l, file: l.file.map((f, j) => (j === i ? { ...f, ...cambi } : f)) } : l));

  const leggi = useCallback(async (id) => {
    const r = await axios.get(`${API}/lotti/${id}`);
    if (attivo.current) setLotto((l) => (l && l.id === id ? { ...l, riepilogo: r.data, errore: null } : l));
    return r.data;
  }, []);

  // opzioni {tipo, personaId}: dalla riga "Carica" di un documento periodico con più file (tre buste paga), ogni file
  // diventa un documento di quel tipo con il suo mese (prima un solo PDF con un mese solo)
  const avvia = useCallback(async (scelti, { tipo, personaId } = {}) => {
    const file = Array.from(scelti || []);
    if (!file.length) return undefined;
    // Un caricamento che si sta aprendo (POST /lotti in volo: con la rete lenta secondi, e i bottoni non sono ancora
    // spenti) non si butta via: prima una seconda scelta di file lo sostituiva senza dirlo e restava un caricamento
    // vuoto ("attesi 2, caricati 0"). Vale per il bottone, il trascinamento e la riga di un documento.
    if (inApertura.current) {
      toast.error(t('documenti.aspettate'));
      return false;
    }
    corrente.current = null;
    seq.current += 1;
    const n = seq.current;
    // Più di 20: non parte niente e lo si dice (prima il 21° veniva scartato in silenzio)
    if (file.length > MAX_FILE) {
      setLotto({ seq: n, id: null, file: [], errore: troppiFile(file.length, lingua), fermo: true });
      return;
    }
    let id;
    inApertura.current = true;
    try {
      const corpo = { quanti: file.length, ...(tipo ? { tipo } : {}), ...(personaId ? { persona_id: personaId } : {}) };
      id = (await axios.post(`${API}/clienti/${clienteId}/lotti`, corpo)).data.id;
    } catch (e) {
      if (seq.current === n) setLotto({ seq: n, id: null, file: [], fermo: true, errore: t('documenti.lotto.nessun_file_caricato', { motivo: motivoErrore(e, null, lingua) }) });
      return undefined;
    } finally {
      inApertura.current = false;
    }
    if (seq.current !== n) return; // nel frattempo è partito un altro caricamento
    const locali = file.map((f) => ({ nome: f.name || 'foto', tipo: f.type, url: f.type.startsWith('image/') ? URL.createObjectURL(f) : null, progresso: 0 }));
    corrente.current = id;
    setLotto({ seq: n, id, tipo: tipo || null, file: locali, riepilogo: null, inizio: Date.now() });
    let prossimo = 0;
    const lavoratore = async () => {
      while (prossimo < file.length) {
        const i = prossimo;
        prossimo += 1;
        try {
          const pronto = await preparaFile(file[i]);
          const modulo = new FormData();
          modulo.append('file', pronto, pronto.name || file[i].name || 'foto.jpg');
          modulo.append('ordine', String(i));
          if (file[i].lastModified) modulo.append('scattata_il', String(file[i].lastModified));
          // la foto raddrizzata (BottoniCarica la segna così) diventa un PDF sul server, come dalla riga "Carica":
          // prima dalla zona libera arrivava come JPEG storto. Il segno sta sul file scelto, non su quello
          // rimpicciolito da preparaFile (che è un File nuovo).
          if (file[i].raddrizzata) modulo.append('come_pdf', 'true');
          await axios.post(`${API}/lotti/${id}/file`, modulo, {
            onUploadProgress: (ev) => { if (ev.total) aggiornaFile(id, i, { progresso: Math.round((ev.loaded / ev.total) * 100) }); },
          });
          aggiornaFile(id, i, { progresso: 100, fatto: true });
        } catch (e) {
          aggiornaFile(id, i, { errore: motivoErrore(e, null, lingua), rete: servizioAssente(e) });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(IN_PARALLELO, file.length) }, lavoratore));
    // "Finito di mandare i file": se la chiamata non va (rete, 5xx) si riprova a ogni giro dell'attesa finché il
    // riepilogo non dice "chiuso". Prima l'errore si ignorava e il lotto restava aperto per sempre (il server ora lo
    // chiude anche da sé quando i file ci sono tutti, o quando è fermo)
    const chiudiLotto = async () => {
      try { await axios.post(`${API}/lotti/${id}/chiudi`); return true; } catch (e) { return definitivo(e); }
    };
    let chiuso = await chiudiLotto();
    const inizio = Date.now();
    let pronto = false;
    while (attivo.current && corrente.current === id && Date.now() - inizio < attesaMassima) {
      if (!chiuso) chiuso = await chiudiLotto();
      let r = null;
      try { r = await leggi(id); } catch (e) { if (definitivo(e)) break; }
      if (r?.chiuso) chiuso = true;
      if (r && r.pronto && r.righe.every((x) => !x.in_analisi)) { pronto = true; if (onPronto) onPronto(r); break; }
      await new Promise((ok) => setTimeout(ok, ogni));
    }
    // Finita l'attesa senza riepilogo (server che non risponde, caricamento sparito): un messaggio e "Chiudi"
    if (!pronto && attivo.current && corrente.current === id) {
      setLotto((lo) => (lo && lo.id === id ? { ...lo, errore: t('documenti.lotto.errore_riepilogo') } : lo));
      if (onPronto) onPronto(null); // la pagina si ricarica: i file arrivati compaiono tra i documenti
    }
  }, [clienteId, leggi, onPronto, attesaMassima, ogni]); // eslint-disable-line

  // "Chiudi" (o "Fatto"): la pagina si rilegge. Le correzioni fatte nel riepilogo (tipo, periodo, di chi è) cambiano
  // quello che manca: prima restava "Ne mancano 3" invece di 2 finché non si ricaricava la pagina
  const chiudi = () => {
    const cera = Boolean(lotto?.id);
    corrente.current = null;
    (lotto?.file || []).forEach((f) => f.url && URL.revokeObjectURL(f.url));
    setLotto(null);
    if (cera && onPronto) onPronto(null);
  };
  return { lotto, avvia, leggi, chiudi };
};

// Un periodo come chiave del menu: il periodo del server e quello delle scelte hanno le chiavi in ordine diverso
// (con JSON.stringify non coincidevano e il periodo scelto compariva due volte: "agosto 2026 | agosto 2026 | …");
// per i movimenti conta l'inizio (la fine è "oggi")
export const chiavePeriodo = (p) => (p ? [p.tipo, p.anno, p.mese, p.trimestre, p.dal].map((x) => x ?? '').join('|') : '');

// Le scelte del menu del periodo, ognuna una volta sola; il periodo già letto dal server c'è anche se è fuori
// dalle scelte di oggi (es. una busta paga di sette mesi fa), con la sua etichetta
export const scelteConAttuale = (scelte, periodo, etichetta) => {
  const uniche = scelte.filter((s, i) => scelte.findIndex((x) => chiavePeriodo(x.periodo) === chiavePeriodo(s.periodo)) === i);
  if (!periodo) return uniche;
  const attuale = { etichetta: etichetta || uniche.find((s) => chiavePeriodo(s.periodo) === chiavePeriodo(periodo))?.etichetta, periodo };
  return [attuale, ...uniche.filter((s) => chiavePeriodo(s.periodo) !== chiavePeriodo(periodo))].filter((s) => s.etichetta);
};

const tipiPer = (variante, tipi) => (tipi && tipi.length ? tipi : CATALOGO.tipi)
  .filter((t) => variante === 'staff' || (t.visibile_cliente && t.chi_carica === 'cliente'));

const Miniatura = ({ riga, file, c }) => {
  const url = (riga.ordini || []).map((o) => file[o]?.url).find(Boolean);
  return (
    <span className={c.mini} aria-hidden="true">
      {url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (riga.content_type || '').includes('pdf') ? 'PDF' : 'FILE'}
    </span>
  );
};

// "Di chi è": il titolare e i familiari con i documenti propri (dalla pratica), col nome di battesimo
const nomeBreve = (p, t) => String(p.nome || '').trim().split(/\s+/)[0] || t('documenti.lotto.titolare');

// Chi può essere l'intestatario del documento: se il server lo dice riga per riga (persone_possibili, anche solo i
// nomi) si usa quell'elenco; se non c'è, come prima: il titolare e i familiari con i documenti propri
export const personePerRiga = (riga, persone = []) => {
  const possibili = riga.persone_possibili;
  if (Array.isArray(possibili) && possibili.length) {
    return possibili.map((p) => (typeof p === 'string' ? { id: p, nome: p }
      : { id: p.id ?? p.persona_id ?? p.nome, nome: p.nome ?? p.etichetta ?? p.id }))
      .filter((p) => p.id);
  }
  return persone.filter((p) => p.id === 'titolare' || p.documenti);
};

const Riga = ({ riga, file, variante, tipi, persone = [], c, onCambia }) => {
  const { t, l: lingua } = useTraduzioni();
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState(null);
  // l'anteprima grande è aperta: i menu stanno lì accanto, non anche nella riga sotto
  const [conAnteprima, setConAnteprima] = useState(false);
  const cambia = async (corpo) => {
    setAttesa(true);
    setErrore(null);
    try {
      await axios.put(`${API}/documenti/${riga.id}/classifica`, corpo);
      await onCambia();
    } catch (e) {
      setErrore(frase(t('documenti.lotto.non_salvato'), e, t('documenti.lotto.riprovate'), lingua));
    } finally { setAttesa(false); }
  };
  const tipo = riga.tipo || riga.proposto;
  const infoTipo = tipiPer('staff', tipi).find((t) => t.id === tipo);
  const regola = (infoTipo?.periodico || {}).regola;
  // i documenti "per persona" (carta d'identità, codice fiscale…) di chi sono: prima la carta d'identità della moglie
  // andava al titolare e prendeva il posto della sua, senza modo di correggerla
  const chi = personePerRiga(riga, persone);
  const conPersona = riga.tipo && (riga.per_persona ?? infoTipo?.per_persona) && chi.length > 1;
  const personaAttuale = riga.persona_id || 'titolare';
  // l'oggi di Roma, come la riga "Carica" della stessa pagina: con l'ora del telefono, a Tokyo alle 23:30 del 30
  // settembre di Roma il menu offriva "settembre 2026" e perdeva "marzo 2026"
  const scelte = regola ? scelteConAttuale(sceltePeriodo(regola, adessoRoma(), lingua), riga.periodo,
    etichettaPeriodo(riga.periodo, riga.periodo_testo, lingua)) : [];
  const stato = riga.in_analisi ? <span className={c.attesa}>{t('documenti.lotto.sto_leggendo')}</span>
    : riga.tipo ? <span className={c.ok}>{t('documenti.lotto.catalogato')}</span>
      : riga.stato === 'da_confermare' ? <span className={c.attesa}>{t('documenti.lotto.da_confermare')}</span>
        : <span className={c.no}>{t('documenti.lotto.pillola_non_riconosciuto')}</span>;
  // I menu per correggere (che documento è, periodo, di chi è): nella riga, e accanto all'anteprima grande quando la
  // si apre (lo staff conferma guardando il documento, mai alla cieca)
  const controlli = !riga.in_analisi && riga.stato !== 'verificato' ? (
    <>
      <select className={c.select} aria-label={t('documenti.lotto.che_documento')} value={riga.tipo || ''} disabled={attesa}
        onChange={(e) => e.target.value && cambia({ tipo: e.target.value })}>
        <option value="">{riga.proposto ? t('documenti.lotto.forse', { nome: riga.proposto_nome }) : t('documenti.lotto.che_documento_q')}</option>
        {tipiPer(variante, tipi).map((t) => <option key={t.id} value={t.id}>{t.singolare || t.nome}</option>)}
      </select>
      {riga.tipo && scelte.length > 0 && (
        <select className={c.select} aria-label={t('documenti.lotto.periodo')} value={chiavePeriodo(riga.periodo)} disabled={attesa}
          onChange={(e) => { const s = scelte.find((x) => chiavePeriodo(x.periodo) === e.target.value); if (s) cambia({ periodo: s.periodo }); }}>
          {!riga.periodo && <option value="">{t('documenti.periodo.quale_lotto')}</option>}
          {scelte.map((s) => <option key={chiavePeriodo(s.periodo)} value={chiavePeriodo(s.periodo)}>{s.etichetta}</option>)}
        </select>
      )}
      {conPersona && (
        <select className={c.select} aria-label={t('documenti.lotto.di_chi')} value={personaAttuale} disabled={attesa} data-testid="persona-riga"
          onChange={(e) => e.target.value !== personaAttuale && cambia({ persona_id: e.target.value })}>
          {!chi.some((p) => p.id === personaAttuale) && <option value={personaAttuale}>{riga.persona}</option>}
          {chi.map((p) => <option key={p.id} value={p.id}>{t('documenti.lotto.di_chi_voce', { nome: nomeBreve(p, t) })}</option>)}
        </select>
      )}
    </>
  ) : null;
  const nomeDocumento = riga.tipo ? riga.testo : riga.nome_originale || riga.nome_file || t('documenti.lotto.questo_documento');
  return (
    <div className={c.riga} data-testid="riga-lotto">
      <ConfermaDocumento compatto documento={{ ...riga, nome_file: riga.nome_file || riga.nome_originale }}
        titolo={nomeDocumento} etichetta={t('documenti.lotto.apri_anteprima', { nome: nomeDocumento })}
        miniatura={<Miniatura riga={riga} file={file} c={c} />}
        azioni={variante === 'staff' ? controlli : null}
        onApri={() => setConAnteprima(variante === 'staff')} onChiudi={() => setConAnteprima(false)} />
      <span className={c.testo}>
        <b>{riga.tipo ? riga.testo : riga.proposto ? t('documenti.lotto.forse_riga', { nome: riga.proposto_nome })
          : riga.in_analisi ? (file[riga.ordini?.[0]]?.nome || t('documenti.lotto.documento')) : t('documenti.lotto.non_riconosciuto')}</b>
        <span className={c.meta}>
          {[riga.parti > 1 ? t('documenti.lotto.foto_unite', { n: riga.parti }) : riga.nome_originale,
            variante === 'staff' && riga.confidenza != null && !riga.in_analisi ? `confidenza ${Math.round(riga.confidenza * 100)}%${riga.fonte === 'mrz' ? ' (MRZ)' : ''}` : null,
          ].filter(Boolean).join(' · ')}
        </span>
        {errore && <span className={c.errore} role="alert">{errore}</span>}
      </span>
      {stato}
      {!conAnteprima && controlli}
    </div>
  );
};

const Riquadro = ({ lotto, variante = 'cliente', tipi, persone, onLeggi, onChiudi, onConfermato }) => {
  const { t, tp, l: lingua } = useTraduzioni();
  const c = STILI[variante];
  const [confermo, setConfermo] = useState(false);
  const [erroreConferma, setErroreConferma] = useState(null);
  const r = lotto.riepilogo;
  const inCaricamento = lotto.file.map((f, i) => ({ ...f, i })).filter((f) => !f.fatto && !f.errore);
  const falliti = lotto.file.map((f, i) => ({ ...f, i })).filter((f) => f.errore);
  const pronto = r && r.pronto && r.righe.every((x) => !x.in_analisi);
  const nomeRiga = r ? r.righe.length : 0;
  const nessuno = pronto && nomeRiga === 0; // tutti i file rifiutati: niente da confermare
  const perRete = falliti.length > 0 && falliti.every((f) => f.rete); // il motivo è la rete, non il formato
  const conferma = async () => {
    setConfermo(true);
    setErroreConferma(null);
    try {
      await axios.post(`${API}/lotti/${lotto.id}/conferma`);
      await onLeggi(lotto.id);
      if (onConfermato) onConfermato();
    } catch (e) {
      setErroreConferma(frase(t('documenti.lotto.conferma_non_riuscita'), e, t('documenti.lotto.riprovate_tra_poco'), lingua));
    } finally { setConfermo(false); }
  };
  const titolo = nessuno || lotto.fermo ? t('documenti.lotto.titolo_nessuno')
    : variante === 'cliente'
      ? t(pronto ? 'documenti.lotto.titolo_pronto' : lotto.errore ? 'documenti.lotto.titolo_non_finito' : 'documenti.lotto.titolo_in_corso')
      : (pronto ? 'Riepilogo del caricamento' : lotto.errore ? 'Caricamento non finito' : 'Caricamento in corso…');
  return (
    <section className={c.scatola} data-testid="caricamento-multiplo" aria-live="polite">
      <div className={c.titolo}>
        {variante === 'cliente' ? <h2>{titolo}</h2> : <span>{titolo}</span>}
      </div>
      {lotto.errore && <p className={c.errore} role={lotto.fermo ? 'alert' : undefined}>{lotto.errore}</p>}
      {inCaricamento.map((f) => (
        <div key={`su-${f.i}`} className={c.riga}>
          <span className={c.mini} aria-hidden="true">{f.url ? <img src={f.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : 'PDF'}</span>
          <span className={c.testo}><b>{f.nome}</b>
            <span className={c.barra} role="progressbar" aria-valuenow={f.progresso} aria-valuemin={0} aria-valuemax={100} aria-label={t('documenti.lotto.caricamento_di', { nome: f.nome })}>
              <i className={c.piena} style={{ width: `${f.progresso}%` }} />
            </span>
          </span>
        </div>
      ))}
      {r && r.righe.map((riga) => <Riga key={riga.id} riga={riga} file={lotto.file} variante={variante} tipi={tipi} persone={persone} c={c} onCambia={() => onLeggi(lotto.id)} />)}
      {falliti.map((f) => (
        <div key={`ko-${f.i}`} className={c.riga}>
          <span className={c.mini} aria-hidden="true">!</span>
          <span className={c.testo}><b>{f.nome}</b><span className={c.errore}>{t('documenti.lotto.non_caricato', { motivo: f.errore })}</span></span>
        </div>
      ))}
      {erroreConferma && <p className={c.errore} role="alert">{erroreConferma}</p>}
      <div className={c.piede}>
        <span className={c.meta}>
          {lotto.fermo ? ''
            : nessuno ? t(perRete ? 'documenti.lotto.nessun_file_rete' : 'documenti.lotto.nessun_file_formato')
              : pronto ? `${tp('comune.plurali.documenti', nomeRiga)}${r.uniti ? ` · ${tp('documenti.plurali.uniti', r.uniti)}` : ''}`
                : t(lotto.errore ? 'documenti.lotto.potete_chiudere' : 'documenti.lotto.potete_continuare')}
        </span>
        <span style={{ display: 'flex', gap: '.5rem' }}>
          {pronto && !nessuno && !r.confermato && <button type="button" className={c.principale} onClick={conferma} disabled={confermo}>{t('documenti.lotto.conferma_tutto')}</button>}
          {/* "Chiudi" c'è sempre: il riquadro non resta mai senza via d'uscita (anche se il server non risponde) */}
          <button type="button" className={c.bottone} onClick={onChiudi}>{t(r?.confermato && !nessuno ? 'comune.fatto' : 'comune.chiudi')}</button>
        </span>
      </div>
    </section>
  );
};

// Ogni caricamento ha il suo riquadro (key = seq): errori di conferma e attese del caricamento di prima non restano
const CaricamentoMultiplo = (props) => <Riquadro key={props.lotto.seq ?? props.lotto.id ?? 'lotto'} {...props} />;

export default CaricamentoMultiplo;
